import { useId } from "react";
import { ArrowUpRight } from "lucide-react";
import { cn } from "../lib/cn.js";

interface Props {
  title: string;
  children: React.ReactNode;
  /** Rendered at the top right, beside the expand control. */
  headerRight?: React.ReactNode;
  /**
   * Omitted means the card has no detail view, so no control is drawn.
   *
   * `| undefined` is spelled out because `exactOptionalPropertyTypes` is on:
   * without it, a caller forwarding an optional prop straight through cannot
   * pass it at all.
   */
  onExpand?: (() => void) | undefined;
  className?: string;
}

/**
 * The panel every widget sits in.
 *
 * `<section aria-labelledby>` rather than a `<div>`, so each card is a named
 * region a screen reader can jump between and the browser suite can scope an
 * assertion to. That is not decoration: the suite's cases work by scoping to
 * a named region and asserting what is, or is not, inside it, and an unnamed
 * `<div>` gives them nothing to hold.
 *
 * **A card title must not begin with a section name.** The browser suite waits
 * on a page's heading by name, and a card heading that starts with the same
 * word matches the same locator -- which is a strict-mode failure, not a near
 * miss. The loading card on the Live view was called "Live updates" and did
 * exactly that; it is "Connection" now. The section names to steer clear of
 * are in `lib/views.ts`.
 */
export function Card({ title, children, headerRight, onExpand, className }: Props) {
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "relative overflow-hidden rounded-card border border-line bg-card p-6 transition-colors",
        "hover:border-line-strong sm:p-7",
        className,
      )}
    >
      <div className="mb-4 flex select-none items-center justify-between gap-4">
        <h3
          id={headingId}
          className="text-sm font-medium tracking-wide text-ink-muted sm:text-[15px]"
        >
          {title}
        </h3>
        <div className="flex items-center gap-2">
          {headerRight}
          {onExpand !== undefined && (
            <button
              type="button"
              onClick={onExpand}
              className="-mr-1.5 rounded-full p-1.5 text-ink-muted transition-colors hover:bg-line hover:text-ink"
            >
              {/* Named, not `title`d. This control is instantiated once per
                  widget, so a `title` here would be one unnamed button per
                  card rather than one mistake. */}
              <span className="sr-only">{`${title} details`}</span>
              <ArrowUpRight size={18} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}
